import { Center } from '@mantine/core';
import { Icon, LoadingAnimation, Modal } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetClientDetailById } from 'api/v3/clients/clients';
import { DetailClientModel } from 'api/v3/models';
import { CreateClient } from 'modules/applications/client/CreateClient';
import { usePermissions } from 'modules/shared/permissions';
import { ItemCardAction } from 'shared/components/ItemCardAction';

type Props = {
    clientId: string;
};

export const DuplicateClientAction = ({ clientId }: Props) => {
    const { t } = useTranslation();
    const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
    const canCreate = usePermissions('Create');

    if (!canCreate) {
        return null;
    }

    return (
        <>
            <ItemCardAction
                icon={<Icon icon="content_copy" />}
                title={t('duplicateItem', { itemType: t('client') })}
                onClick={() => {
                    setIsDuplicateModalOpen(true);
                }}
            />
            {isDuplicateModalOpen && (
                <DuplicateClientModal clientId={clientId} onClose={() => setIsDuplicateModalOpen(false)} />
            )}
        </>
    );
};

type DuplicateItemModelProps = {
    clientId: string;
    onClose: () => void;
};

const DuplicateClientModal = ({ clientId, onClose: handleClose }: DuplicateItemModelProps) => {
    const { t } = useTranslation();
    const { data: clientDetails } = useGetClientDetailById(clientId, undefined);

    // Images are not copied: their URIs point to blob storage, which the browser cannot download (no CORS).
    const duplicatedClientDetails: DetailClientModel | undefined = clientDetails?.data.basicInformation
        ? {
              ...clientDetails.data,
              basicInformation: {
                  ...clientDetails.data.basicInformation,
                  clientPictureUri: undefined,
                  clientBackgroundUri: undefined,
                  id: '',
                  name: `${clientDetails.data.basicInformation.name} - copy`,
              },
          }
        : undefined;

    return (
        <Modal
            closeOnClickOutside={false}
            opened={true}
            size={1000}
            title={t('duplicateItem', { itemType: t('client') })}
            centered
            onClick={(event) => {
                event.stopPropagation();
            }}
            onClose={handleClose}
        >
            {!duplicatedClientDetails ? (
                <Center>
                    <LoadingAnimation />
                </Center>
            ) : (
                <CreateClient initialValue={duplicatedClientDetails} onCancel={handleClose} />
            )}
        </Modal>
    );
};
